import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Bug, MessageCircle, Send } from 'lucide-react';

interface BugReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const BugReportDialog = ({ open, onOpenChange }: BugReportDialogProps) => {
  const { t } = useTranslation();
  const [type, setType] = useState<'bug' | 'support'>('bug');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuth();
  const location = useLocation();

  const handleSubmit = async () => {
    if (!message.trim()) {
      toast.error(t('support.pleaseDescribe'));
      return;
    }

    const submittedEmail = email.trim() || user?.email;
    if (!submittedEmail) {
      toast.error(t('support.pleaseProvideEmail'));
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke('send-telegram', {
        body: {
          type,
          message: message.trim(),
          userEmail: submittedEmail,
          page: location.pathname,
          userAgent: navigator.userAgent,
        },
      });

      if (error) throw error;

      const ticketId = data?.ticketId;
      const telegramSent = data?.telegramSent;
      
      if (ticketId) {
        const statusMsg = telegramSent 
          ? t('support.reportSent') 
          : t('support.reportSavedOnly');
        toast.success(`${statusMsg}\n${t('support.ticketId')}: ${ticketId.substring(0, 8)}...`);
      } else {
        toast.success(t('support.reportSent'));
      }
      
      setMessage('');
      setEmail('');
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error sending report:', error);
      toast.error(t('support.reportFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('support.reportIssue')}</DialogTitle>
          <DialogDescription>
            {t('support.reportDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Type Selection */}
          <RadioGroup value={type} onValueChange={(v) => setType(v as 'bug' | 'support')}>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="bug" id="bug" />
              <Label htmlFor="bug" className="flex items-center gap-2 cursor-pointer">
                <Bug className="h-4 w-4" />
                {t('support.bugReport')}
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="support" id="support" />
              <Label htmlFor="support" className="flex items-center gap-2 cursor-pointer">
                <MessageCircle className="h-4 w-4" />
                {t('support.supportRequest')}
              </Label>
            </div>
          </RadioGroup>

          {/* Email */}
          <div>
            <Label htmlFor="email">{t('support.email')} *</Label>
            <Input
              id="email"
              type="email"
              placeholder={user?.email || t('support.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2"
            />
            {!user?.email && (
              <p className="text-xs text-muted-foreground mt-1">
                {t('support.emailRequired')}
              </p>
            )}
          </div>

          {/* Message */}
          <div>
            <Label htmlFor="message">{t('support.description')} *</Label>
            <Textarea
              id="message"
              placeholder={t('support.descriptionPlaceholder')}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              className="mt-2"
            />
          </div>

          {/* Submit Button */}
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !message.trim()}
            className="w-full"
          >
            <Send className="mr-2 h-4 w-4" />
            {isSubmitting ? t('support.sending') : t('support.sendReport')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
